import type {
  ApplicationAccess,
  ApplicationCreateInput,
  ApplicationUpdateInput,
  MeDTO,
} from '@masterapps/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { App } from '@/lib/catalog-config';

/**
 * Aplicação como a API devolve: a linha da tabela + o bloco `access` (unidades,
 * setores, matrículas e o toggle de confidencialidade). As listas só vêm
 * preenchidas para administrador; para colaborador chegam vazias, então o campo
 * é opcional aqui e a tela de edição trata a ausência como "sem restrição".
 */
export type AppWithAccess = App & { access?: ApplicationAccess };

export function useApplications() {
  return useQuery<App[]>({
    queryKey: ['applications'],
    queryFn: async () => {
      const res = await apiClient.api.applications.$get();
      if (!res.ok) throw await readApiError(res, 'Falha ao carregar aplicações');
      const json = (await res.json()) as { data: App[] };
      return json.data;
    },
  });
}

export function useApplication(slug: string | undefined) {
  return useQuery<AppWithAccess>({
    queryKey: ['application', slug],
    enabled: !!slug,
    queryFn: async () => {
      const res = await apiClient.api.applications[':slug'].$get({
        param: { slug: slug as string },
      });
      if (!res.ok) throw await readApiError(res, 'Aplicação não encontrada');
      const json = (await res.json()) as { data: AppWithAccess };
      return json.data;
    },
  });
}

async function fetchMe(): Promise<MeDTO> {
  const res = await apiClient.api.me.$get();
  if (!res.ok) throw await readApiError(res, 'Falha ao carregar perfil');
  const json = (await res.json()) as { data: MeDTO };
  return json.data;
}

export function useMe() {
  return useQuery<MeDTO>({
    queryKey: ['me'],
    queryFn: fetchMe,
  });
}

// Variante "fresca" da query ['me'], para decisões de ROTEAMENTO (AccessGate e
// telas de primeiro acesso/aguardando): sempre revalida ao montar, para nunca
// rotear com status/matrícula desatualizados do cache. A Sidebar e o restante
// do chrome continuam no useMe() com cache normal — mesma queryKey.
export function useMeFresh() {
  return useQuery<MeDTO>({
    queryKey: ['me'],
    queryFn: fetchMe,
    staleTime: 0,
    refetchOnMount: 'always',
  });
}

// Envelope de erro padrão da API: { error, code, details? }.
export interface ApiErrorBody {
  error: string;
  code?: string;
  details?: {
    attemptsLeft?: number;
    retryAfterSec?: number;
    // 400 VALIDATION_ERROR — um item por campo recusado pelo Zod na API.
    issues?: Array<{ campo: string; mensagem: string }>;
  };
}

// Erro tipado do client: preserva status HTTP, code e details do envelope para
// a UI tratar cada caso (lockout, tentativas restantes etc.) sem parse ad hoc.
export class ApiError extends Error {
  status: number;
  code?: string;
  // `NonNullable` e não `ApiErrorBody['details']` cru: a propriedade de origem já
  // é opcional, então o tipo dela carrega `undefined` e o `?` daqui repetiria o
  // mesmo estado por dois caminhos (ausente vs. presente-como-undefined).
  details?: NonNullable<ApiErrorBody['details']>;

  constructor(status: number, body: ApiErrorBody) {
    super(body.error);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.code;
    this.details = body.details;
  }
}

/**
 * Extrai a primeira mensagem LEGÍVEL de um corpo de erro arbitrário. O corpo
 * nem sempre é o envelope da API: um validator pode devolver
 * `{ error: [<issues>] }` e um proxy pode devolver HTML. Nunca interpolamos
 * objeto em string — era daí que saía "[object Object]" no toast.
 */
function firstMessage(value: unknown): string | undefined {
  if (typeof value === 'string') return value.trim() || undefined;
  if (Array.isArray(value)) {
    for (const item of value) {
      const found = firstMessage(item);
      if (found) return found;
    }
    return undefined;
  }
  if (typeof value === 'object' && value !== null) {
    const o = value as Record<string, unknown>;
    return firstMessage(o.error) ?? firstMessage(o.message) ?? firstMessage(o.mensagem);
  }
  return undefined;
}

// Converte qualquer resposta !ok em ApiError, preservando status/code/details
// quando o envelope padrão vier completo. `fallback` é a mensagem de UI usada
// quando o corpo não tem nada legível (204, HTML, JSON inválido).
export async function readApiError(res: Response, fallback: string): Promise<ApiError> {
  const body = (await res.json().catch(() => null)) as unknown;
  const envelope = (typeof body === 'object' && body !== null ? body : {}) as {
    error?: unknown;
    code?: unknown;
    details?: unknown;
  };
  const details = envelope.details;
  return new ApiError(res.status, {
    error: firstMessage(envelope.error) ?? firstMessage(body) ?? fallback,
    code: typeof envelope.code === 'string' ? envelope.code : undefined,
    details:
      typeof details === 'object' && details !== null && !Array.isArray(details)
        ? (details as ApiErrorBody['details'])
        : undefined,
  });
}

// Onboarding por matrícula (primeiro acesso): vincula o cadastro TOTVS ao
// usuário logado. O corpo de erro é o envelope JSON acima — o hono client não
// o tipa, então readApiError lê res.json() defensivamente.
export function useOnboarding() {
  const qc = useQueryClient();
  return useMutation<MeDTO, ApiError, string>({
    mutationFn: async (matricula: string) => {
      const res = await apiClient.api.me.onboarding.$post({ json: { matricula } });
      if (!res.ok) {
        throw await readApiError(res, 'Falha ao enviar a matrícula. Tente novamente.');
      }
      const json = (await res.json()) as { data: MeDTO };
      return json.data;
    },
    onSuccess: (data) => {
      // Atualiza o cache imediatamente (o gate roteia com o valor novo) e
      // revalida em seguida para garantir consistência com o servidor.
      qc.setQueryData(['me'], data);
      qc.invalidateQueries({ queryKey: ['me'] });
    },
  });
}

export function useCreateApplication() {
  const qc = useQueryClient();
  return useMutation<App, ApiError, ApplicationCreateInput>({
    mutationFn: async (input: ApplicationCreateInput) => {
      const res = await apiClient.api.applications.$post({ json: input });
      // 400 VALIDATION_ERROR vem com a mensagem do 1º campo recusado; o toast
      // do chamador usa err.message (e pode detalhar com err.details.issues).
      if (!res.ok) throw await readApiError(res, 'Falha ao publicar aplicação');
      const json = (await res.json()) as { data: App };
      return json.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['applications'] });
    },
  });
}

export interface UpdateApplicationVars {
  /** UUID da aplicação · o PATCH é por id, não por slug. */
  id: string;
  input: ApplicationUpdateInput;
}

/**
 * `PATCH /api/applications/:id` (admin).
 *
 * ATENÇÃO ao bloco `access` do payload: enviá-lo com listas vazias APAGA as
 * associações de unidade/setor/colaborador; omiti-lo PRESERVA o que está
 * gravado. Quem monta o payload decide (ver a regra documentada em
 * `components/screens/register-screen.tsx`).
 */
export function useUpdateApplication() {
  const qc = useQueryClient();
  return useMutation<AppWithAccess, ApiError, UpdateApplicationVars>({
    mutationFn: async ({ id, input }: UpdateApplicationVars) => {
      const res = await apiClient.api.applications[':id'].$patch({ param: { id }, json: input });
      if (!res.ok) throw await readApiError(res, 'Falha ao salvar as alterações');
      const json = (await res.json()) as { data: AppWithAccess };
      return json.data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['applications'] });
      // A query individual é por SLUG e o PATCH pode ter mudado o slug: invalida
      // todas as ['application', *] por prefixo e semeia o cache do slug atual.
      qc.invalidateQueries({ queryKey: ['application'] });
      qc.setQueryData(['application', data.slug], data);
    },
  });
}

export interface SetApresentacaoVars {
  /** UUID da aplicação · o PATCH é por id. */
  id: string;
  /** Estado DESEJADO da coluna: `true` = fora do deck e do PDF. */
  desabilitada: boolean;
}

/** Só o que o rollback precisa: o valor que a lista tinha ANTES do clique. */
interface SetApresentacaoContext {
  anterior: boolean | undefined;
}

/**
 * Chave das mutações desta coluna. Existe para `isMutating`: "Marcar todas" e
 * "Nenhuma" do lobby disparam UM PATCH por plataforma alterada, e só a última a
 * terminar pode revalidar a lista — ver `onSettled`.
 */
const APRESENTACAO_MUTATION_KEY = ['applications', 'apresentacao'] as const;

/**
 * Grava `apresentacaoDesabilitada` de UMA aplicação — é o clique na lista
 * "Plataformas" do lobby da apresentação (`PATCH /api/applications/:id`, admin).
 *
 * Otimista, como `useToggleFavorite`: a caixinha vira no clique, sem esperar a
 * rede, porque a lista tem ~50 linhas e quem está montando o deck marca várias
 * em sequência. Diferenças em relação ao favorito, as duas pelo LOTE:
 *
 *  · o rollback devolve SÓ a linha que falhou ao valor que ela tinha — restaurar
 *    um snapshot da lista inteira desfaria as outras marcações ainda em voo;
 *  · a revalidação só sai da ÚLTIMA mutação a terminar (`isMutating === 1`).
 *    Sem isso, a primeira resposta de um lote de 49 dispararia um refetch que
 *    voltaria com o estado ANTIGO das outras 48 e apagaria o otimismo delas até
 *    a próxima resposta — a lista piscaria linha a linha.
 *
 * Sem toast aqui: quem chama decide o texto (`mutate(vars, { onError })`). O
 * módulo é carregado pelos testes ANTES de existir DOM, e `sonner` olha
 * `window` no corpo do módulo — é por isso que `use-favorites` entra lá por
 * `import()` tardio.
 */
export function useSetApresentacaoDesabilitada() {
  const qc = useQueryClient();

  /** Escreve o flag de `id` na lista `['applications']`; o resto fica intacto. */
  const escrever = (id: string, desabilitada: boolean) =>
    qc.setQueryData<App[]>(['applications'], (lista: App[] | undefined) =>
      lista?.map((a: App) => (a.id === id ? { ...a, apresentacaoDesabilitada: desabilitada } : a)),
    );

  return useMutation<AppWithAccess, ApiError, SetApresentacaoVars, SetApresentacaoContext>({
    mutationKey: APRESENTACAO_MUTATION_KEY,
    mutationFn: async ({ id, desabilitada }: SetApresentacaoVars) => {
      const res = await apiClient.api.applications[':id'].$patch({
        param: { id },
        json: { apresentacaoDesabilitada: desabilitada },
      });
      if (!res.ok) throw await readApiError(res, 'Falha ao salvar a marcação da apresentação');
      const json = (await res.json()) as { data: AppWithAccess };
      return json.data;
    },
    onMutate: async ({ id, desabilitada }) => {
      // Refetch em voo não pode sobrescrever o valor otimista.
      await qc.cancelQueries({ queryKey: ['applications'] });
      const anterior = qc
        .getQueryData<App[]>(['applications'])
        ?.find((a: App) => a.id === id)?.apresentacaoDesabilitada;
      escrever(id, desabilitada);
      return { anterior };
    },
    // Vale o que o servidor gravou (é a mesma coluna que a ficha edita).
    onSuccess: (data) => escrever(data.id, data.apresentacaoDesabilitada),
    onError: (_error, { id }, context) => {
      if (context?.anterior !== undefined) escrever(id, context.anterior);
    },
    onSettled: () => {
      if (qc.isMutating({ mutationKey: APRESENTACAO_MUTATION_KEY }) !== 1) return;
      void qc.invalidateQueries({ queryKey: ['applications'] });
      // A ficha (`['application', slug]`) mostra o mesmo check na edição.
      void qc.invalidateQueries({ queryKey: ['application'] });
    },
  });
}

/** `DELETE /api/applications/:id` (admin). As associações de acesso caem por FK. */
export function useDeleteApplication() {
  const qc = useQueryClient();
  return useMutation<{ id: string }, ApiError, string>({
    mutationFn: async (id: string) => {
      const res = await apiClient.api.applications[':id'].$delete({ param: { id } });
      if (!res.ok) throw await readApiError(res, 'Falha ao excluir a aplicação');
      const json = (await res.json()) as { data: { id: string } };
      return json.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['applications'] });
      qc.invalidateQueries({ queryKey: ['application'] });
    },
  });
}
